import { FormattedMessage, useIntl } from "react-intl";

import UFOSegment from "@atlaskit/react-ufo/segment";

import BannerAlert, { BannerProps } from "@/components/wpl/banner-alert/src";
import { DEFAULT_VALUES, useLocalizedHref } from "@/libraries/web-framework";
import {
    EnhancedBannerAlert,
    EnhancedBannerProps
} from "@/patterns/adg4/enhanced-banner-alert/enhanced-banner-alert";
import { GlobalFooter } from "@/patterns/global-footer/src/index";
import StickyContainer from "@/patterns/sticky-container/sticky-container";
import { experiments, useExperiment } from "@/utils/experiment";
import { ExperimentView } from "@/utils/experiment";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import JsmUseCaseProductTourOnWac from "./jsm-use-case/sections/hero";
import GWP6480JSMEmbeddedSignup from "./jsm-use-case/sections/hero-embedded-signup";
import HeroTabs from "./jsm-use-case/sections/hero-tabs";
import { AtlassianIntelligenceITSM } from "./sections/ai-itsm";
import CardsSection from "./sections/cards";
import CTASection from "./sections/cta";
import CustomersSection from "./sections/customers";
import IntegrationSection from "./sections/integration";
import ScalingSection from "./sections/scaling";
import JiraServiceManagementSoftwareNavV2 from "./sections/software-jira-service-management-navbarv2";
import TabsSection from "./sections/tabs";
import { Team25LiftedBanner } from "./sections/team25-lifted-banner";
import TilesSection from "./sections/tiles";

const Gwp5090BannerExperimentIsActive = () => {
    const gwp5090BannerExperiment = useExperiment(
        experiments.gwp5090ExperimentBannerJSM
    );
    return (
        gwp5090BannerExperiment != undefined &&
        gwp5090BannerExperiment.type === "enrolled" &&
        gwp5090BannerExperiment.value === "butterbar"
    );
};

export const JSMAboveNavButterbar = () => {
    const intl = useIntl();

    const gwp5090Team25Config: EnhancedBannerProps = {
        identifier: "GWP5090.jsm-product-tour.butterbar",
        text: (
            <>
                <FormattedMessage
                    id="team25-banners-and-butterbars.jsm.text"
                    defaultMessage="<strong>April 8-10 • Anaheim, California</strong>Be the first to demo our latest AI innovations and deliver game-changing experiences at Team &rsquo;25!"
                    values={DEFAULT_VALUES}
                />
            </>
        ),
        mobileText: (
            <>
                <FormattedMessage
                    id="team25-banners-and-butterbars.jsm.mobile-text"
                    defaultMessage="<strong>April 8-10 • Anaheim, California</strong>Demo our latest AI tools at Team &rsquo;25!"
                    values={DEFAULT_VALUES}
                />
            </>
        ),
        productKey: "home",
        backgroundPreset: "gwp-5090-banners",
        imageProps: {
            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/4360jv8l7u81b0w6nn37fu86a2131624.webp",
            alt: "Team25", //Event name is not localized
            width: 200
        },
        linkText: intl.formatMessage({
            id: "team25-banners-and-butterbars.jsm.cta",
            defaultMessage: "Get your pass"
        }),
        linkUrl:
            "https://events.atlassian.com/team?utm_medium=event-web-referral&utm_source=jsm_product-tour&event_context=701QB00000Lg6oBYAR&utm_campaign=K:event|H:any|N:Team_25|P:agnostic|O:event-marketing|F:pre-event|C:event-home&utm_content=butterbar-banner_WEBTEAM-17396&event_id=216653",
        dataEventProps: {
            "data-product-key": "home",
            "data-event-component": "linkButton",
            "data-event-container": "announcementBanner",
            "data-event": "clicked",
            "data-label": intl.formatMessage({
                id: "team25-banners-and-butterbars.jsm.cta",
                defaultMessage: "Get your pass"
            }),
            "data-label-english": "Get your pass"
        }
    };

    return Gwp5090BannerExperimentIsActive() ? (
        <EnhancedBannerAlert {...gwp5090Team25Config} />
    ) : null;
};

export const LandingBannerAlert = () => {
    const intl = useIntl();

    const showForrester = checkFeatureFlagGate(
        featureFlags.gwp6701ForresterBanner
    );

    const showAiBanner = checkFeatureFlagGate(
        featureFlags.wacUipButterBarJsmGate
    );

    const forresterBannerConfig: BannerProps = {
        identifier: "jira-service-management-software.banner.forrester-devops",
        text: "Atlassian recognized as a Leader in The Forrester Wave™: DevOps Platforms, Q2 2025.",
        linkText: "Learn more",
        linkUrl: useLocalizedHref(
            "/forrester/forrester-wave-devops?utm_source=in-product-banner&utm_medium=banner&utm_campaign=P:bitbucket*O:clm*I:banner-wac-jiraservicemanagement*Y:cloud_mig*E:dc-cloud*&utm_sfdc-campaign_id=701QB00000SlywgYAB"
        ),
        dataEventProps: {
            "data-product-key": "jiraServiceManagement",
            "data-event-component": "linkButton",
            "data-event-container": "announcementBanner",
            "data-event": "clicked"
        }
    };

    const aiBannerMessages: BannerProps = {
        identifier:
            "jira-service-management-software.atlassian-intelligence_exp",
        text: intl.formatMessage({
            id: "jira-service-management-software.banner-alert.atlassian-intelligence_exp",
            defaultMessage:
                "Transform teamwork with Atlassian Intelligence - now available."
        }),
        linkText: intl.formatMessage({
            id: "jira-service-management-software.link-text",
            defaultMessage: "Learn more"
        }),
        linkUrl: useLocalizedHref("/software/artificial-intelligence"),
        dataEventProps: {
            "data-product-key": "jiraServiceManagement",
            "data-event-component": "linkButton",
            "data-event-container": "announcementBanner",
            "data-event": "clicked",
            "data-label": "Learn more",
            "data-label-english": "Learn more"
        }
    };

    if (Gwp5090BannerExperimentIsActive()) {
        return null;
    }
    if (showForrester) {
        return <BannerAlert {...forresterBannerConfig} />;
    }
    if (showAiBanner) {
        return <BannerAlert {...aiBannerMessages} />;
    }

    return null;
};

const HeroSection = () => {
    return (
        <UFOSegment name="jsm-landing-hero">
            <JsmUseCaseProductTourOnWac />
        </UFOSegment>
    );
};

export const JiraServiceManagementSoftwarePageTemplate = () => {
    return (
        <div>
            <StickyContainer>
                <JSMAboveNavButterbar />
                <JiraServiceManagementSoftwareNavV2 />
            </StickyContainer>
            <UFOSegment name="jsm-landing-banner">
                <LandingBannerAlert />
            </UFOSegment>
            <ExperimentView
                experiment={experiments.gwp6480JSMEmbeddedSignup}
                render={(isVariant) => {
                    if (isVariant === true) {
                        return (
                            <UFOSegment name="jsm-hero-embedded-signup">
                                <GWP6480JSMEmbeddedSignup />
                            </UFOSegment>
                        );
                    }
                    return (
                        <UFOSegment name="jsm-landing-hero">
                            <JsmUseCaseProductTourOnWac />
                        </UFOSegment>
                    );
                }}
            />
            <UFOSegment name="jsm-landing-hero-tabs">
                <HeroTabs />
            </UFOSegment>
            <UFOSegment name="jsm-lifted-banner-team25">
                <Team25LiftedBanner />
            </UFOSegment>
            <UFOSegment name="jsm-landing-aiItsmSection">
                <AtlassianIntelligenceITSM />
            </UFOSegment>
            <UFOSegment name="jsm-landing-tabsSection">
                <TabsSection />
            </UFOSegment>
            <UFOSegment name="jsm-landing-customersSection">
                <CustomersSection />
            </UFOSegment>
            <UFOSegment name="jsm-landing-scalingSection">
                <ScalingSection />
            </UFOSegment>
            <UFOSegment name="jsm-landing-tilesSection">
                <TilesSection />
            </UFOSegment>
            <UFOSegment name="jsm-landing-cardsSection">
                <CardsSection />
            </UFOSegment>
            <UFOSegment name="jsm-landing-integrationSection">
                <IntegrationSection />
            </UFOSegment>
            <UFOSegment name="jsm-landing-ctaSection">
                <CTASection />
            </UFOSegment>
            <UFOSegment name="jsm-landing-globalFooter">
                <GlobalFooter />
            </UFOSegment>
        </div>
    );
};
